import { Link, NavLink, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { Instagram, Facebook, Mail, Menu, X, Star, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { applyTheme } from "@/lib/themeApplier";
import { api } from "@/lib/api";
import { tenantHref, useTenant } from "@/lib/tenant";

const MIN_REVIEWS_TO_SHOW = 3;

const RAW_NAV_ITEMS = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/booking", label: "Book" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout() {
  const { settings } = useSiteSettings();
  const { slug: tenantSlug } = useTenant();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // When mounted under /s/:slug, rewrite every nav link so it lives inside
  // the tenant's URL space. Outside the tenant tree, the helper is a no-op.
  const navItems = RAW_NAV_ITEMS.map((n) => ({ ...n, to: tenantHref(n.to) }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!settings) return undefined;
    return applyTheme({
      themeId: settings.theme_id || "editorial",
      overrides: settings.theme_overrides || {},
    });
  }, [settings]);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* ── Navigation ─────────────────────────────────────────────────── */}
      <header
        className={`sticky top-0 z-40 transition-all duration-400 ${
          scrolled
            ? "bg-background/97 backdrop-blur-xl border-b border-border shadow-[0_1px_12px_rgba(0,0,0,0.06)]"
            : "bg-background/80 backdrop-blur-xl border-b border-border"
        }`}
        data-testid="public-header"
      >
        <div className="container-x flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to={tenantHref("/")}
            className="flex items-center shrink-0"
            data-testid="brand-logo-link"
            onClick={() => setMenuOpen(false)}
          >
            <Logo className="h-8 md:h-12" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="group relative text-xs uppercase tracking-[0.22em] transition-colors pb-1"
              >
                {({ isActive }) => (
                  <>
                    <span className={`transition-colors ${isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"}`}>
                      {n.label}
                    </span>
                    {/* Animated gold underline */}
                    <span
                      className={`absolute bottom-0 left-0 h-px bg-[hsl(var(--gold))] transition-all duration-300 ease-out ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                      aria-hidden="true"
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Book CTA */}
          <Link
            to={tenantHref("/booking")}
            className="hidden md:inline-flex items-center gap-2 px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors group"
            data-testid="header-book-btn"
          >
            Book a Session
            <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 -mr-2 text-foreground"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <div
            className="md:hidden border-t border-border bg-background/97 backdrop-blur-xl animate-fade-in"
            data-testid="mobile-menu"
          >
            <nav className="container-x py-4 flex flex-col">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3.5 border-b border-border text-sm uppercase tracking-[0.22em] flex items-center justify-between ${
                      isActive ? "text-foreground" : "text-muted-foreground"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {n.label}
                      {isActive && <span className="w-4 h-px bg-[hsl(var(--gold))]" />}
                    </>
                  )}
                </NavLink>
              ))}
              <Link
                to={tenantHref("/booking")}
                onClick={() => setMenuOpen(false)}
                className="mt-5 inline-flex items-center justify-center gap-2 h-11 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors"
                data-testid="mobile-book-btn"
              >
                Book a Session <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* ── Page content ───────────────────────────────────────────────── */}
      <main className="flex-1 page-enter">
        <Outlet />
      </main>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer className="bg-[#0D0C0B] text-stone-300" data-testid="public-footer">
        <FooterRating />

        <div className="container-x pt-16 pb-12 grid md:grid-cols-4 gap-10 md:gap-14">
          {/* Brand */}
          <div>
            <Logo className="h-11 brightness-0 invert opacity-80" testid="footer-logo" />
            <p className="text-sm text-stone-400 mt-5 max-w-xs leading-relaxed">
              Editorial photography studio. Crafting timeless images for the moments that matter.
            </p>
            {/* Social */}
            <div className="flex gap-4 mt-6">
              {settings?.instagram_url && (
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-9 h-9 border border-stone-700 flex items-center justify-center text-stone-400 hover:text-stone-100 hover:border-stone-400 transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {settings?.facebook_url && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="w-9 h-9 border border-stone-700 flex items-center justify-center text-stone-400 hover:text-stone-100 hover:border-stone-400 transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              <a
                href={`mailto:${settings?.contact_email || "hello@allureimage.com"}`}
                aria-label="Email"
                className="w-9 h-9 border border-stone-700 flex items-center justify-center text-stone-400 hover:text-stone-100 hover:border-stone-400 transition-colors"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Studio links */}
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-stone-500 mb-5">Studio</div>
            <ul className="space-y-3 text-sm">
              {[
                { to: "/services", label: "Services" },
                { to: "/portfolio", label: "Portfolio" },
                { to: "/booking", label: "Book a session" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={tenantHref(l.to)}
                    className="text-stone-400 hover:text-stone-100 transition-colors inline-flex items-center gap-1.5 group"
                  >
                    <span className="w-0 h-px bg-[hsl(var(--gold))] group-hover:w-3 transition-all duration-200" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Hours */}
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-stone-500 mb-5">Visit</div>
            <p className="text-sm text-stone-400 leading-relaxed">By appointment only</p>
            <p className="text-sm text-stone-400 mt-2">Mon – Sat · 10:00 – 18:00</p>
            <p className="text-sm text-stone-400 mt-2">{settings?.contact_location || "New Jersey"}</p>
          </div>

          {/* Contact info */}
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-stone-500 mb-5">Connect</div>
            <div className="space-y-3 text-sm text-stone-400">
              <a
                href={`mailto:${settings?.contact_email || "hello@allureimage.com"}`}
                className="block hover:text-stone-100 transition-colors"
              >
                {settings?.contact_email || "hello@allureimage.com"}
              </a>
              {settings?.contact_phone && (
                <p>{settings.contact_phone}</p>
              )}
            </div>
            <Link
              to={tenantHref("/booking")}
              className="mt-8 inline-flex items-center gap-2 px-5 h-10 bg-stone-800 text-stone-200 text-xs uppercase tracking-[0.22em] hover:bg-stone-700 transition-colors group border border-stone-700"
            >
              Book a Session
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" />
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-stone-800/60">
          <div className="container-x py-5 flex items-center justify-between text-xs text-stone-600">
            <span>© {new Date().getFullYear()} {settings?.studio_name || "Allure Image"}. All rights reserved.</span>
            <Link
              to="/admin/login"
              className="hover:text-stone-300 transition-colors"
              data-testid="footer-admin-login"
            >
              Studio Manager
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterRating() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/reviews/stats")
      .then((r) => { if (!cancelled) setStats(r.data); })
      .catch(() => { if (!cancelled) setStats(null); });
    return () => { cancelled = true; };
  }, []);

  if (!stats || (stats.count || 0) < MIN_REVIEWS_TO_SHOW) return null;

  const avg = Number(stats.average) || 0;
  const count = stats.count;
  const fullStars = Math.floor(avg);
  const hasHalf = avg - fullStars >= 0.5;

  return (
    <Link
      to={tenantHref("/#testimonials")}
      className="block border-b border-stone-800 hover:bg-stone-800/40 transition-colors"
      data-testid="footer-rating"
    >
      <div className="container-x py-5 flex flex-wrap items-center justify-center gap-3 text-sm">
        <div className="flex items-center gap-0.5" aria-label={`Average rating ${avg} out of 5`}>
          {[...Array(5)].map((_, i) => {
            const filled = i < fullStars || (i === fullStars && hasHalf);
            return (
              <Star
                key={`footer-star-${i}`}
                className={`w-4 h-4 ${filled ? "fill-[hsl(var(--gold))] text-[hsl(var(--gold))]" : "text-stone-600"}`}
                strokeWidth={1.5}
                data-testid={`footer-star-${i}`}
              />
            );
          })}
        </div>
        <span className="font-medium tracking-wide text-stone-300" data-testid="footer-rating-average">
          {avg.toFixed(1)}
        </span>
        <span className="text-stone-500">
          from{" "}
          <span className="text-stone-300" data-testid="footer-rating-count">
            {count}
          </span>{" "}
          happy {count === 1 ? "client" : "clients"}
        </span>
      </div>
    </Link>
  );
}
